/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { assertNotNull } from './assert';
import { NG_HOST_SYMBOL, createError, createViewState, directive, enterView, hostElement, leaveView, locateHostElement, renderComponentOrTemplate } from './instructions';
import { domRendererFactory3 } from './renderer';
import { notImplemented, stringify } from './util';
/**
 * Options that control how the component should be bootstrapped.
 * @record
 */
export function CreateComponentOptions() { }
function CreateComponentOptions_tsickle_Closure_declarations() {
    /**
     * Which renderer factory to use.
     * @type {?|undefined}
     */
    CreateComponentOptions.prototype.rendererFactory;
    /**
     * Host element on which the component will be bootstrapped. If not specified,
     * the component definition's `tag` is used to query the existing DOM for the
     * element to bootstrap.
     * @type {?|undefined}
     */
    CreateComponentOptions.prototype.host;
    /**
     * Module injector for the component. If unspecified, the injector will be NULL_INJECTOR.
     * @type {?|undefined}
     */
    CreateComponentOptions.prototype.injector;
    /**
     * List of features to be applied to the created component. Features are simply
     * functions that decorate a component with a certain behavior.
     *
     * Example: PublicFeature is a function that makes the component public to the DI system.
     * @type {?|undefined}
     */
    CreateComponentOptions.prototype.features;
}
/**
 * Bootstraps a component, then creates and returns a `ComponentRef` for that component.
 *
 * @template T
 * @param {?} componentType Component to bootstrap
 * @param {?} opts
 * @return {?}
 */
export function createComponentRef(componentType, opts) {
    const /** @type {?} */ component = renderComponent(componentType, opts);
    const /** @type {?} */ hostView = createViewRef(() => detectChanges(component), component);
    return {
        location: { nativeElement: getHostElement(component) },
        injector: opts.injector || NULL_INJECTOR,
        instance: component,
        hostView: hostView,
        changeDetectorRef: hostView,
        componentType: componentType,
        // TODO: implement destroy and onDestroy
        destroy: () => { },
        onDestroy: (cb) => { }
    };
}
/**
 * Creates an EmbeddedViewRef.
 *
 * @template T
 * @param {?} detectChanges The detectChanges function for this view
 * @param {?} context The context for this view
 * @return {?} The EmbeddedViewRef
 */
function createViewRef(detectChanges, context) {
    return addDestroyable(new EmbeddedViewRef(detectChanges), context);
}
class EmbeddedViewRef {
    /**
     * @param {?} detectChanges
     */
    constructor(detectChanges) {
        this.detectChanges = detectChanges;
        // TODO: rootNodes should be replaced when properly implemented
        this.rootNodes = /** @type {?} */ ((null));
    }
    /**
     * @return {?}
     */
    markForCheck() {
        if (ngDevMode) {
            throw notImplemented();
        }
    }
    /**
     * @return {?}
     */
    detach() {
        if (ngDevMode) {
            throw notImplemented();
        }
    }
    /**
     * @return {?}
     */
    checkNoChanges() {
        if (ngDevMode) {
            throw notImplemented();
        }
    }
    /**
     * @return {?}
     */
    reattach() {
        if (ngDevMode) {
            throw notImplemented();
        }
    }
    /**
     * @return {?}
     */
    destroy() { }
    /**
     * @param {?} cb
     * @return {?}
     */
    onDestroy(cb) { }
}
function EmbeddedViewRef_tsickle_Closure_declarations() {
    /** @type {?} */
    EmbeddedViewRef.prototype.rootNodes;
    /** @type {?} */
    EmbeddedViewRef.prototype.context;
    /** @type {?} */
    EmbeddedViewRef.prototype.destroyed;
    /** @type {?} */
    EmbeddedViewRef.prototype.detectChanges;
}
/**
 * Interface for destroy logic. Implemented by addDestroyable.
 * @record
 */
function DestroyRef() { }
function DestroyRef_tsickle_Closure_declarations() {
    /** @type {?} */
    DestroyRef.prototype.context;
    /**
     * Whether or not this object has been destroyed
     * @type {?}
     */
    DestroyRef.prototype.destroyed;
    /**
     * Destroy the instance and call all onDestroy callbacks.
     * @type {?}
     */
    DestroyRef.prototype.destroy;
    /**
     * Register callbacks that should be called onDestroy
     * @type {?}
     */
    DestroyRef.prototype.onDestroy;
}
/**
 * Decorates an object with destroy logic (implementing the DestroyRef interface)
 * and returns the enhanced object.
 *
 * @template T, C
 * @param {?} obj The object to decorate
 * @param {?} context
 * @return {?} The object with destroy logic
 */
function addDestroyable(obj, context) {
    let /** @type {?} */ destroyFn = null;
    obj.destroyed = false;
    obj.destroy = function () {
        destroyFn && destroyFn.forEach((fn) => fn());
        this.destroyed = true;
    };
    obj.onDestroy = (fn) => (destroyFn || (destroyFn = [])).push(fn);
    obj.context = context;
    return obj;
}
// TODO: A hack to not pull in the NullInjector from @angular/core.
export const /** @type {?} */ NULL_INJECTOR = {
    get: (token, notFoundValue) => {
        throw new Error('NullInjector: Not found: ' + stringify(token));
    }
};
/**
 * Bootstraps a Component into an existing host element and returns an instance
 * of the component.
 *
 * @template T
 * @param {?} componentType Component to bootstrap
 * @param {?=} opts
 * @return {?}
 */
export function renderComponent(componentType, opts = {}) {
    const /** @type {?} */ rendererFactory = opts.rendererFactory || domRendererFactory3;
    const /** @type {?} */ componentDef = componentType.ngComponentDef;
    let /** @type {?} */ component;
    const /** @type {?} */ hostNode = locateHostElement(rendererFactory, opts.host || componentDef.tag);
    const /** @type {?} */ oldView = enterView(createViewState(-1, rendererFactory.createRenderer(hostNode, componentDef.rendererType), []), /** @type {?} */ ((null)));
    try {
        // Create element node at index 0 in data array
        hostElement(hostNode, componentDef);
        // Create directive instance with n() and store at index 1 in data array (el is 0)
        component = directive(1, componentDef.n(), componentDef);
    }
    finally {
        leaveView(oldView);
    }
    opts.features && opts.features.forEach((feature) => feature(component, componentDef));
    detectChanges(component);
    return component;
}
/**
 * @template T
 * @param {?} component
 * @return {?}
 */
export function detectChanges(component) {
    ngDevMode && assertNotNull(component, 'component');
    const /** @type {?} */ hostNode = /** @type {?} */ ((/** @type {?} */ (component))[NG_HOST_SYMBOL]);
    if (ngDevMode && !hostNode) {
        createError('Not a directive instance', component);
    }
    ngDevMode && assertNotNull(hostNode.data, 'hostNode.data');
    renderComponentOrTemplate(hostNode, hostNode.view, component);
    isDirty = false;
}
let /** @type {?} */ isDirty = false;
/**
 * @template T
 * @param {?} component
 * @param {?=} scheduler
 * @return {?}
 */
export function markDirty(component, scheduler = requestAnimationFrame) {
    ngDevMode && assertNotNull(component, 'component');
    if (!isDirty) {
        isDirty = true;
        scheduler(() => detectChanges(component));
    }
}
/**
 * @template T
 * @param {?} component
 * @return {?}
 */
export function getHostElement(component) {
    return (/** @type {?} */ ((/** @type {?} */ (component))[NG_HOST_SYMBOL])).native;
}
//# sourceMappingURL=component.js.map